"use client";

import { useEffect } from "react";
import { MenuIcon, PlusIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@award-party/shared";
import { BrandMark } from "@/components/brand-mark";
import { refreshNativeStatusBarMeta } from "@/lib/status-bar-meta";

const PARTY_ROW_COUNT = 3;

const SEASON_STAT_KEYS = [
  "activeNow",
  "awaitingResults",
  "partiesAllTime",
  "friendsAllTime",
] as const;

export function PartiesListSkeleton() {
  const t = useTranslations("NativeHome");
  const tCommon = useTranslations("Common");
  const tParties = useTranslations("Parties");

  // A light-mode theme-color media query makes WebKit paint black status-bar
  // icons on this ink screen. Strip it while the skeleton is up.
  useEffect(() => {
    refreshNativeStatusBarMeta(document);
  }, []);

  // The launch shell paints a gold wash through the status-bar inset.
  // Pull the ink background up over that inset so loading matches My Parties.
  return (
    <main
      aria-busy="true"
      className="pointer-events-none -mt-[max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px))] min-h-0 flex-1 overflow-hidden bg-(--mk-ink) pt-[max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px))] lg:ml-56"
      data-testid="parties-list-skeleton"
    >
      <p className="sr-only" role="status">
        {t("openingParties")}
      </p>

      <div
        aria-hidden="true"
        className="flex h-14 items-center justify-between border-b border-(--mk-cream-08) px-4 lg:hidden"
        data-testid="parties-skeleton-mobile-bar"
      >
        <div className="flex items-center gap-2.5">
          <BrandMark size={32} className="h-8 w-8" />
          <span className="font-serif text-lg font-bold tracking-tight text-(--mk-cream)">
            {tCommon("brand")}
          </span>
        </div>
        <span
          className="rounded-lg p-2 text-(--mk-cream-60)"
          data-testid="parties-skeleton-menu"
        >
          <MenuIcon className="h-5 w-5" />
        </span>
      </div>

      <aside
        aria-hidden="true"
        className="fixed inset-y-0 left-0 z-40 hidden w-56 flex-col border-r border-(--mk-cream-08) bg-(--mk-ink) ap-pad-safe-top lg:flex"
        data-testid="parties-skeleton-sidebar"
      >
        <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-(--mk-cream-08) px-5">
          <BrandMark size={36} className="h-9 w-9" />
          <span className="font-serif text-xl font-bold tracking-tight text-(--mk-cream)">
            {tCommon("brand")}
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-2 px-3 py-6">
          <SkeletonBlock className="h-10 w-full rounded-lg" />
          <SkeletonBlock className="h-10 w-full rounded-lg" />
        </div>
        <div className="border-t border-(--mk-cream-08) px-3 py-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <SkeletonBlock className="size-8 rounded-full" />
            <SkeletonBlock className="h-3.5 flex-1" />
          </div>
        </div>
      </aside>

      <div className="mx-auto w-full max-w-7xl px-5 py-6 sm:px-8 lg:px-12">
        <div className="mb-8 flex items-center justify-between gap-4 lg:items-start">
          <div className="min-w-0">
            <p className="mb-1.5 hidden text-[11px] font-bold uppercase tracking-widest text-(--mk-gold) lg:block">
              {tParties("dashboard")}
            </p>
            <h1 className="font-serif text-3xl font-black leading-none tracking-tight text-(--mk-cream) lg:text-5xl">
              {tParties("title")}
            </h1>
            <p className="mt-2 hidden text-sm text-(--mk-cream-60) lg:block">
              {tParties("subtitle")}
            </p>
          </div>
          <div
            aria-hidden="true"
            className="flex size-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-(--mk-gold) text-(--mk-ink) shadow-[0_4px_16px_rgba(201,168,76,0.25)] sm:w-auto sm:px-5"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden text-sm font-semibold sm:block">
              {tParties("createOrJoin")}
            </span>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_16.75rem] lg:items-start lg:gap-7">
          <div className="min-w-0">
            <section
              aria-hidden="true"
              className="party-stage relative isolate mb-7 overflow-hidden rounded-3xl border border-(--mk-gold-border) p-5 sm:p-7 lg:p-8"
            >
              <div className="party-stage-rays" />
              <div className="relative z-10">
                <div className="mb-8 flex items-center justify-between gap-3">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-(--mk-gold)">
                    {tParties("upNext")}
                  </p>
                  <SkeletonBlock className="h-3 w-24" />
                </div>
                <div className="flex flex-col gap-5">
                  <div className="flex min-w-0 items-center gap-4">
                    <SkeletonBlock className="size-[88px] shrink-0 rounded-xl" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <SkeletonBlock className="h-7 w-3/5 max-w-xs lg:h-9" />
                      <SkeletonBlock className="h-3.5 w-2/5 max-w-xs" />
                      <SkeletonBlock className="h-3 w-1/2 max-w-sm" />
                    </div>
                    <SkeletonBlock className="size-5 shrink-0 rounded-sm" />
                  </div>
                  <SkeletonBlock
                    tone="gold"
                    className="h-11 w-full rounded-xl sm:h-12 sm:w-44"
                  />
                </div>
              </div>
            </section>

            <div
              aria-hidden="true"
              className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-(--mk-cream-08) bg-(--mk-ink-soft) px-3.5 py-2.5 lg:hidden"
              data-testid="parties-skeleton-mobile-stats"
            >
              <SkeletonBlock className="h-3.5 w-36" />
              <span className="text-[13px] font-semibold text-(--mk-gold)">
                {tParties("showStats")}
              </span>
            </div>

            <div
              aria-hidden="true"
              className="mb-4 flex items-center gap-1 border-b border-(--mk-cream-08)"
            >
              <div className="-mb-px flex items-center gap-1.5 border-b-2 border-(--mk-gold) px-4 py-2.5 text-sm font-medium text-(--mk-cream)">
                {tParties("active")}
                <SkeletonBlock className="h-4 w-5 rounded-full" />
              </div>
              <div className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-(--mk-cream-35)">
                {tParties("allParties")}
                <SkeletonBlock className="h-4 w-5 rounded-full" />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {Array.from({ length: PARTY_ROW_COUNT }, (_, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className="flex items-center gap-3.5 rounded-xl border border-(--mk-cream-08) bg-(--mk-ink-soft) px-3.5 py-3 lg:gap-4 lg:px-4 lg:py-3.5"
                  data-testid="party-row-skeleton"
                >
                  <SkeletonBlock className="size-11 shrink-0 rounded-lg" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <SkeletonBlock className="h-4 w-28 max-w-[55%]" />
                      <SkeletonBlock className="h-4 w-12 shrink-0 rounded-full" />
                    </div>
                    <SkeletonBlock className="mt-2 h-3 w-40 max-w-[75%] lg:hidden" />
                    <SkeletonBlock className="mt-2 hidden h-3 w-48 lg:block" />
                  </div>
                  <div className="hidden shrink-0 items-center gap-6 lg:flex">
                    <SkeletonBlock className="h-3 w-28" />
                    <SkeletonBlock className="h-3 w-24" />
                  </div>
                  <SkeletonBlock className="size-4 shrink-0 rounded-sm" />
                </div>
              ))}
            </div>
          </div>

          <aside
            aria-hidden="true"
            className="hidden lg:sticky lg:top-8 lg:flex lg:w-[16.75rem] lg:shrink-0 lg:flex-col lg:gap-3"
            data-testid="parties-skeleton-season"
          >
            <div className="rounded-xl border border-(--mk-cream-08) bg-(--mk-ink-soft) px-4 py-3.5">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-(--mk-gold)">
                {tParties("yourSeason")}
              </p>
              <div>
                {SEASON_STAT_KEYS.map((key) => (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-3 py-1.5 first:pt-0 last:pb-0"
                  >
                    <span className="text-[13px] text-(--mk-cream-35)">
                      {tParties(key)}
                    </span>
                    <SkeletonBlock className="h-5 w-6" />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-(--mk-cream-08) bg-(--mk-ink-soft) px-4 py-3.5">
              <p className="mb-1.5 text-[11px] font-bold uppercase tracking-widest text-(--mk-gold)">
                {tParties("browseShows")}
              </p>
              <SkeletonBlock className="mb-2 h-3.5 w-full" />
              <SkeletonBlock className="mb-3 h-3.5 w-4/5" />
              <span className="text-[13px] font-semibold text-(--mk-gold)">
                {tParties("seeAllShows")}
              </span>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

function SkeletonBlock({
  className,
  tone = "neutral",
}: {
  className?: string;
  tone?: "neutral" | "gold";
}) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "animate-pulse rounded-md motion-reduce:animate-none",
        tone === "gold"
          ? "bg-[color-mix(in_srgb,var(--mk-gold)_42%,transparent)]"
          : "bg-(--mk-cream-15)",
        className,
      )}
    />
  );
}
